import { computed, ComputedRef } from 'vue'
import { RouteLocation, useRoute, useRouter } from 'vue-router'
import { usePermissions } from '@/store/profile/permissions'

/**
 * Checks whether a user can visit a given route based on their permissions.
 * @param route
 * @param permissions
 * @param opts
 * @param opts.allowOnPermissionsLoading
 */
function canVisitRoute(
  route: RouteLocation,
  permissions: ReturnType<typeof usePermissions>,
  opts: { allowOnPermissionsLoading: boolean }
): boolean {
  const meta = route.meta
  if (!meta.permissionCheck) return true
  if (!permissions.isLoaded.value) return opts.allowOnPermissionsLoading
  return permissions[meta.permissionCheck].value
}

/**
 * Composable for checking route authorization in components.
 * Looks up a route by path and checks if the current user is authorized to access it,
 * using the same permission logic as the router's authorizationGuard.
 *
 * Returns a ComputedRef so callers can use it as a reactive signal.
 */
export function useRouteAuthz() {
  const router = useRouter()
  const route = useRoute()
  const permissions = usePermissions()

  /**
   * Whether the current route is authorized for the user.
   */
  const isCurrentRouteAuthorized = computed(() => {
    return checkIfRouteAuthorized(route.path).value
  })

  /**
   * Checks if a specific route is authorized for the user.
   * @param path The path of the route to check.
   */
  function checkIfRouteAuthorized(path: string): ComputedRef<boolean> {
    return computed(() => {
      const route = router.resolve(path)
      return canVisitRoute(route, permissions, {
        allowOnPermissionsLoading: false,
      })
    })
  }

  return { isCurrentRouteAuthorized, checkIfRouteAuthorized }
}
